import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { grupoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";

const Grupo = () => {
  const [loading, setLoading] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [showGrupoForm, setShowGrupoForm] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState(null);
  const [grupoForm, setGrupoForm] = useState({ nome: "" });
  const [grupoSearchTerm, setGrupoSearchTerm] = useState("");
  const [paginacaoGrupo, setPaginacaoGrupo] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async (nome = "") => {
    setLoading(true);
    try {
      const data = await grupoService.listarGrupo(nome || undefined);
      setGrupos(data || []);
      setPaginacaoGrupo((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleGrupoSearch = (e) => {
    const value = e.target.value;
    setGrupoSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadGrupos(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const totalGrupos = grupos.length;
  const totalPaginasGrupo = Math.ceil(
    totalGrupos / paginacaoGrupo.itensPorPagina
  );
  const indiceInicialGrupo =
    (paginacaoGrupo.paginaAtual - 1) * paginacaoGrupo.itensPorPagina;
  const indiceFinalGrupo = indiceInicialGrupo + paginacaoGrupo.itensPorPagina;
  const gruposPaginados = grupos.slice(indiceInicialGrupo, indiceFinalGrupo);

  const irParaPrimeiraPaginaGrupo = () => {
    setPaginacaoGrupo((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPaginaGrupo = () => {
    setPaginacaoGrupo((prev) => ({ ...prev, paginaAtual: totalPaginasGrupo }));
  };

  const irParaPaginaAnteriorGrupo = () => {
    setPaginacaoGrupo((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPaginaGrupo = () => {
    setPaginacaoGrupo((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasGrupo, prev.paginaAtual + 1),
    }));
  };

  const irParaPaginaGrupo = (numeroPagina) => {
    setPaginacaoGrupo((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginasGrupo = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacaoGrupo.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginasGrupo, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleGrupoSubmit = async () => {
    if (!grupoForm.nome.trim()) {
      showError("Nome do grupo e obrigatorio");
      return;
    }

    setLoading(true);

    try {
      if (editingGrupo) {
        await grupoService.atualizarGrupo(editingGrupo.id, grupoForm);
        showSuccess("Grupo atualizado com sucesso!");
      } else {
        await grupoService.cadastrarGrupo(grupoForm);
        showSuccess("Grupo criado com sucesso!");
      }

      await loadGrupos(grupoSearchTerm);
      handleGrupoCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar grupo");
    } finally {
      setLoading(false);
    }
  };

  const handleGrupoEdit = (item) => {
    setEditingGrupo(item);
    setGrupoForm({ nome: item.nome || "" });
    setShowGrupoForm(true);
  };

  const handleGrupoDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este grupo?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await grupoService.deletarGrupo(id);
      showSuccess("Grupo excluido com sucesso!");
      await loadGrupos(grupoSearchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir grupo");
    } finally {
      setLoading(false);
    }
  };

  const handleGrupoCancel = () => {
    setShowGrupoForm(false);
    setEditingGrupo(null);
    setGrupoForm({ nome: "" });
  };

  const handleGrupoNew = () => {
    setEditingGrupo(null);
    setGrupoForm({ nome: "" });
    setShowGrupoForm(true);
  };

  const handleGrupoKeyPress = (e) => {
    if (e.key === "Enter") {
      handleGrupoSubmit();
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Grupos</h1>
        <p className="text-gray-600">Gerencie os grupos utilizados no sistema</p>
      </div>

      <div>
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar por nome..."
              value={grupoSearchTerm}
              onChange={handleGrupoSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={handleGrupoNew}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Novo Grupo
          </button>
        </div>

        {showGrupoForm && (
          <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">
              {editingGrupo ? "Editar Grupo" : "Novo Grupo"}
            </h3>

            <div>
              <div className="mb-4">
                <label
                  htmlFor="grupo-nome"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Nome *
                </label>
                <input
                  type="text"
                  id="grupo-nome"
                  value={grupoForm.nome}
                  onChange={(e) =>
                    setGrupoForm({ ...grupoForm, nome: e.target.value })
                  }
                  onKeyPress={handleGrupoKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite o nome do grupo"
                  maxLength={100}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleGrupoSubmit}
                  disabled={loading || !grupoForm.nome.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save size={16} />
                  {loading ? "Salvando..." : "Salvar"}
                </button>

                <button
                  onClick={handleGrupoCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <X size={16} />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold">Lista de Grupos</h3>
            <span className="text-sm text-gray-500">
              {totalGrupos} registro(s) encontrado(s)
            </span>
          </div>

          {loading && grupos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Carregando grupos...</div>
          ) : grupos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {grupoSearchTerm ? "Nenhum grupo encontrado" : "Nenhum grupo cadastrado"}
            </div>
          ) : (
            <>
              <div>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Grupo
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {gruposPaginados.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.nome}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleGrupoEdit(item)}
                              disabled={loading}
                              className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleGrupoDelete(item.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPaginasGrupo > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Mostrando {indiceInicialGrupo + 1} ate {Math.min(indiceFinalGrupo, totalGrupos)} de {totalGrupos} registros
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={irParaPrimeiraPaginaGrupo}
                        disabled={paginacaoGrupo.paginaAtual === 1}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Primeira página"
                      >
                        <ChevronsLeft size={16} />
                      </button>

                      <button
                        onClick={irParaPaginaAnteriorGrupo}
                        disabled={paginacaoGrupo.paginaAtual === 1}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Página anterior"
                      >
                        <ChevronLeft size={16} />
                      </button>

                      <div className="flex space-x-1">
                        {gerarNumerosPaginasGrupo().map((numeroPagina) => (
                          <button
                            key={numeroPagina}
                            onClick={() => irParaPaginaGrupo(numeroPagina)}
                            className={`px-3 py-2 rounded-md text-sm font-medium ${
                              paginacaoGrupo.paginaAtual === numeroPagina
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {numeroPagina}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={irParaProximaPaginaGrupo}
                        disabled={paginacaoGrupo.paginaAtual === totalPaginasGrupo}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Próxima página"
                      >
                        <ChevronRight size={16} />
                      </button>

                      <button
                        onClick={irParaUltimaPaginaGrupo}
                        disabled={paginacaoGrupo.paginaAtual === totalPaginasGrupo}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Última página"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Grupo;
